import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {FormUtils} from "../../../../../shared/utils/form-utils";
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {ProductVariantsDialogComponent} from "./product-variants-dialog/product-variants-dialog.component";
import {EcommerceService} from "../../ecommerce.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ProductAddHelperService} from "./product-add-helper.service";
import {
  IAddProductForm,
  IAttribute,
  ICategory,
  IOption, IShippingClass,
  ITag, ITaxClass, ITemplate,
  IVariation
} from "./add-product-form.interface";
import {IEcommerceProduct, IProductsSetting} from "../../ecommerce-product.interface";
import {filter, first, map, takeUntil, tap} from "rxjs/operators";
import {Observable} from "rxjs";
import {of} from "rxjs";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {Subject} from "rxjs";
import {EcommerceAnalyticsLabel} from "../../ecommerce-product.enum";
import {DbModalService} from "../../../../../modules/modal/modal";
import {ProductVariantsHelper} from "./product-variants-helper.service";

@Component({
    selector: 'db-add-product',
    templateUrl: './add-product.component.html',
    styleUrls: ['./add-product.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [ProductVariantsHelper],
    standalone: false
})
export class AddProductComponent implements OnInit, OnDestroy {
  productForm: FormGroup = this._productAddHelper.getProductForm();

  private _defaultFormValues = this.productForm.getRawValue();
  website = this._activatedRoute.snapshot.parent.parent.parent.data['website'];
  categories: ICategory[] = [];
  tags: ITag[] = [];
  attributes: IAttribute[] = [];
  templates: ITemplate[] = [];
  taxClasses: ITaxClass[] = [];
  shippingClassList: IShippingClass[] = [];
  variantsDialogForm = this._fb.group({
    variants: this._fb.array([]),
    saved: false
  });
  loading = false;
  promoteProductList: IEcommerceProduct[] = [];
  productId = this._activatedRoute.snapshot.params['productId'];
  isEdit = !!this.productId;
  product: IEcommerceProduct;
  variants: IVariation[] = [];
  activeAttributes$: Observable<IOption[]> = of([]);
  activeAttributes: IOption[] = [];
  productUrl;
  isPublished = false;
  saveAction = this.isEdit ? this._updateProduct : this._createProduct;
  private _subscription$: Subject<void> = new Subject<void>();
  private _analyticsActionName = 'Ecommerce - Products';
  originalFormValues = {};
  originalVariants: IVariation[] = [];

  get descriptionForm(): FormGroup {
    return this.productForm.get('description_group') as FormGroup;
  }

  get mediaForm(): FormGroup {
    return this.productForm.get('media_group') as FormGroup;
  }

  get pricingForm(): FormGroup {
    return this.productForm.get('pricing_group') as FormGroup;
  }

  get variantsForm(): FormGroup {
    return this.productForm.get('variants_group') as FormGroup;
  }

  get inventoryForm(): FormGroup {
    return this.productForm.get('inventory_group') as FormGroup;
  }

  get linkedProductsForm(): FormGroup {
    return this.productForm.get('linked_products_group') as FormGroup;
  }

  get shippingForm(): FormGroup {
    return this.productForm.get('shipping_group') as FormGroup;
  }

  get taxForm(): FormGroup {
    return this.productForm.get('tax_class') as FormGroup;
  }

  get categoryForm(): FormGroup {
    return this.productForm.get('category_group') as FormGroup;
  }

  constructor(private _fb: FormBuilder,
              public _productAddHelper: ProductAddHelperService,
              private _ecommerceService: EcommerceService,
              private _activatedRoute: ActivatedRoute,
              private _router: Router,
              private _store: Store<fromStore.DbStoreState>,
              private _productVariantsHelper: ProductVariantsHelper,
              private _modalService: DbModalService) {
  }

  ngOnInit() {
    this._listenLoading();
    this._setHelperProps();
    this._store.dispatch(new fromStore.LoadEcommerceProductDropdowns({websiteId: this.website.id}));
    this._selectDropdownList();
    if (this.isEdit) {
      this.loading = true;
      this._selectProduct();
    }
    this._listenAttributesChange();
  }

  private _discardChanges() {
    // (this.variantsForm.get('variants') as FormArray).clear();
    this.productForm.patchValue(this.originalFormValues);
    this.variants = [...this.originalVariants];
    this._setVariations();
  }

  private _setHelperProps() {
    this._productVariantsHelper.productForm = this.productForm;
    this._productVariantsHelper.variantsForm = this.variantsDialogForm;
  }

  private _selectDropdownList() {
    this._store.select(fromStore.getDropdowns(this.website.id))
      .pipe(
        filter(dropdowns => !!dropdowns),
        first()
      ).subscribe(dropdowns => {
      this._setAutocompleteValues(dropdowns);
      if (this.productId) {
        const params = {websiteId: this.website.id, id: this.productId};

        this._store.dispatch(new fromStore.LoadEcommerceProduct(params));
        this._store.dispatch(new fromStore.LoadEcommerceProductVariations(params));
      }
    });
  }

  private _listenLoading() {
    this._store.select(fromStore.getProductLoading(this.website.id))
      .pipe(takeUntil(this._subscription$))
      .subscribe((loading: boolean) => {
        if (loading !== undefined) {
          this.loading = loading;
        }
      });
  }

  private _selectProduct() {
    this._store.select(fromStore.getProduct(this.website.id))
      .pipe(
        filter((product) => !!product),
        first()
      ).subscribe(product => {
      this.product = product;
      this.product.template_id = product.selected_template_id;
      this.isPublished = product.status === 'publish';
      this.productUrl = `${this.website.site_url}/product/${product.slug}`;
      const {
        category,
        subcategory
      } = this._productAddHelper.sortCategories(this.product.categories, this.categories);
      this.product.category = category;
      this.product.subcategory = subcategory;
      this.product.template = this._productAddHelper.sortTemplates(this.product.template_id, this.templates);
      this._createEmptyForms();
      const data = this._productAddHelper.transformProductToForm(this.product, this.promoteProductList);
      this.originalFormValues = data;
      this.productForm.patchValue(data);
      this._defaultFormValues = this.productForm.getRawValue();
      if (this.product.variations.length) {
        this._selectVariants();
        this._updateView();
      } else if (this.product.has_options) {
        this.markAllOptionValuesDirty();
      }
    });
  }

  private _updateView() {
    setTimeout(() => {
      document.getElementsByClassName('add-product-wrapper').item(0).parentElement.click();
    });
  }

  markAllOptionValuesDirty() {
    const itemsArray = this.variantsForm.get('variants') as FormArray;

    itemsArray.controls.forEach((itemGroup) => {
      const optionValueArray = itemGroup.get('option_value') as FormArray;

      optionValueArray.controls.forEach((control) => {
        control.markAsDirty();
      });
    });
  }

  private _selectVariants() {
    this._store.select(fromStore.getVariations(this.website.id))
      .subscribe(data => {
        if (!data) {
          return;
        }
        const variants = data.sort((a, b) => a.id - b.id);
        this.variants = variants;
        this.originalVariants = [...variants];
        this._setVariations();
      });
  }

  private _setVariations() {
    const {variants, saved} = this.variantsDialogForm.getRawValue();
    if (saved) {
      variants.map((variant: any, index) => {
        if (this.variants[index]) {
          this.variants[index] = {
            ...this.variants[index], ...variant, dimensions: {
              width: variant.width,
              height: variant.height,
              length: variant.length
            }
          };
        } else {
          this.variants.push(
            {
              attributes: this.activeAttributes[index],
              ...variant, dimensions: {
                width: variant.width,
                height: variant.height,
                length: variant.length
              }
            }
          );
        }
      });
    }
    this._productVariantsHelper.clearVariantsArray();
    this._productVariantsHelper.variants = this.variants;
    this._productAddHelper.addAttributeIdInOptions(this.attributes, this.activeAttributes);
    const optionsList = this._productVariantsHelper.combineOptions(this.activeAttributes);
    console.log(this.activeAttributes, optionsList);
    this._productVariantsHelper.addVariants(optionsList);
    this.variants = this.variantsDialogForm.getRawValue().variants as IVariation[];
  }

  private _createEmptyForms() {
    this.product.images.forEach((image) => {
      (this.mediaForm.get('images') as FormArray).push(this._productAddHelper.createImageControl(image));
    });
    this.product.tags.forEach(() => {
      (this.categoryForm.get('collection') as FormArray).push(this._productAddHelper.createOptionControl());
    });
    this.product.upsell_ids.forEach(() => {
      (this.linkedProductsForm.get('upsells') as FormArray).push(this._productAddHelper.createOptionControl());
    });
    this.product.cross_sell_ids.forEach(() => {
      (this.linkedProductsForm.get('cross_sells') as FormArray).push(this._productAddHelper.createOptionControl());
    });
    this.product.attributes.forEach((att, index) => {
      const variants = this.variantsForm.get('variants') as FormArray;
      variants.push(this._productAddHelper.variantFormTemplate());
    });
    this.product.attributes.forEach((att, index) => {
      const variants = this.variantsForm.get('variants') as FormArray;
      const option = (variants.at(index).get('option_value') as FormArray);
      att.options.forEach(() => option.push(this._fb.group({name: ''})));
    });
  }

  private _setAutocompleteValues(data) {
    this.categories = data.categories;
    this.tags = data.tags;
    this.promoteProductList = data.products;
    this.attributes = data.products_attributes;
    this.templates = this._setDefaultTemplateInDropdown(data.single_templates, data?.all_products_template_id);
    this.taxClasses = data.tax_classes;
    this.shippingClassList = data.shipping_class_list;
    const settings = data.settings as IProductsSetting;
    this.productForm.get('tax_class').setValue({name: "Standard rate", slug: "standard"});
    this._productAddHelper.measurements.next({
      weight_unit: data.weight_unit,
      dimension_unit: data.dimension_unit
    });
    this._productAddHelper.currency.next({symbol: data.currency_symbol});
    this.categoryForm.get('template').get('id').setValue(data?.all_products_template_id);
    if (settings.products) {
      this.shippingForm.get('weight_type').setValue(settings.products.woocommerce_weight_unit.value);
    }
  }

  private _setDefaultTemplateInDropdown(templates: { title: string; id: number }[], defaultId: number): {
    title: string;
    id: number
  }[] {
    const checkDefault = templates.some(temp => temp.id === defaultId);
    if (!checkDefault) {
      templates.push({
        id: defaultId,
        title: 'Single product'
      })
    }
    return templates;
  }

  applyVariations() {
    this._saveAttributes(this.activeAttributes, false);
  }

  onShowVariations(options: IOption[], openEdit = true) {
    this._saveAttributes(options, openEdit);
  }

  openVariantsModal(options: IOption[]) {
    const instance = this._modalService
      .open(ProductVariantsDialogComponent);
    instance.componentInstance.options = options;
    instance.componentInstance.websiteId = this.website.id;
    instance.componentInstance.productForm = this.productForm;
    instance.componentInstance.originalVariantsForm = this.variantsDialogForm;
    instance.componentInstance.variants = this.variants;
  }

  private async _saveAttributes(options: IOption[], openVariations = true) {
    const data = {
      create: [],
    };
    options.forEach((option: IOption) => {
      if (!option.option.value || option.option.value > 10000000) {
        data.create.push({name: option.option.label});
      }
    });
    return await new Promise((resolve, reject) => {
      if (data.create[0]) {
        this._ecommerceService.batchCreateAttributes(this.website.id, data)
          .subscribe((response) => {
            if (response && response.success) {
              const attributes: IAttribute[] = response.data.dataKey.create;
              this.attributes = [...this.attributes, ...attributes];
              this._productAddHelper.addAttributeIdInOptions(this.attributes, options);
              this.variantsForm.get('variants').patchValue(options);
              this._setVariations();
              this._updateView();
              if (openVariations) {
                this.openVariantsModal(options);
              }
              resolve(null);
            }
          });
      } else {
        this._productAddHelper.addAttributeIdInOptions(this.attributes, options);

        this._setVariations();
        resolve(null);
        if (openVariations) {
          this.openVariantsModal(options);
        }
      }
    });
  }

  createCategory(value: { name: string, parent?: number }) {
    const id = new Date().getTime();
    this.categories = [...this.categories, {id, name: value.name, parent: value.parent}];
    this._updateCategoriesForm(value, id);
    this._ecommerceService.createCategory(this.website.id, value)
      .subscribe((resp) => {
        if (resp && resp.success) {
          this.categories = this.categories.map(cat => {
            if (cat.id === id) {
              cat.id = resp.data.dataKey.id;
            }
            return cat;
          });
          this._updateCategoriesForm(value, resp.data.dataKey.id);
        }
      });
  }

  createTag(tag: { name: string, index: number }) {
    this._ecommerceService.createTag(this.website.id, {name: tag.name})
      .subscribe((resp) => {
        if (resp && resp.success) {
          const formArray = (this.categoryForm.get('collection') as FormArray);
          this.tags = [...this.tags, resp.data.dataKey];
          formArray.at(tag.index).setValue({id: resp.data.dataKey.id, name: tag.name});
        }
      });
  }

  private _updateCategoriesForm(value, id) {
    const controlName = value.parent ? 'subcategory' : 'category';
    this.categoryForm.get(controlName).setValue({
      label: value.name,
      id
    });
  }

  private _createVariations(productId: string) {
    const variations = this._productAddHelper.processVariantsFormData(this.variantsDialogForm.getRawValue().variants as any);
    if (!variations.create.length && !variations.update.length) {
      return;
    }
    this._ecommerceService.batchUpdateVariations(this.website.id, productId, variations)
      .subscribe((resp) => {
        if (resp && resp.success) {
          this.loading = false;
        }
      });
  }

  private _setDefaultWeightType() {
    if (this.shippingForm.get('weight_type').pristine) {
      return;
    }
    const data = {value: this.shippingForm.get('weight_type').value};
    this._ecommerceService.setDefaultWeightType(this.website.id, data)
      .subscribe((resp) => {
      });
  }

  async saveProduct(draft = false, updateStatus = true) {
    if (this.loading) {
      return;
    }
    if (updateStatus) {
      this.descriptionForm.get('status').setValue(draft ? 'draft' : 'active');
    }
    console.log(this.descriptionForm.get('status').value, updateStatus);
    this.setTitleValidation();
    const form = new FormUtils();
    form.markAllAsTouched(this.productForm);
    if (!this.productForm.invalid) {
      this.loading = true;
      await this.saveAction();
    }
  }

  private setTitleValidation() {
    if (!this.descriptionForm.get('name').value || !(this.descriptionForm.get('name').value && this.descriptionForm.get('name').value.trim())) {
      this.descriptionForm.get('name').setErrors({required: true});
      return;
    }
    this.descriptionForm.get('name').setErrors(null);
  }

  private _listenAttributesChange() {
    this.activeAttributes$ = this.variantsForm.get('variants').valueChanges
      .pipe(
        map((variants) => variants.filter((variant, i) => {
            if (variant.option_value &&
              variant.option &&
              variant.option.label) {
              variant.option.line_id = variant.option.value || i;
              return true;
            }
            return false;
          }
        )),
        tap((options) => {
          this.activeAttributes = options;
        })
      );
  }

  private async _updateProduct() {
    const form = new FormUtils();
    const changedValues: IAddProductForm = form.getDirtyValues(this.productForm) as IAddProductForm;
    changedValues.type = this.productForm.get('type').value;
    let images = [];
    let attributes: any[];

    if (changedValues.media_group && changedValues.media_group.images.length) {
      images = await this._productAddHelper.uploadImages(changedValues, this.website.id);
    }

    if (changedValues.variants_group && this.activeAttributes.length) {
      await this._saveAttributes(this.activeAttributes, false);
    }

    attributes = this._productAddHelper.processAttributesForm(this.variantsForm.getRawValue().variants);
    const data = this._productAddHelper.processFormData(changedValues, images, attributes);
    this._ecommerceService.updateProduct(this.website.id, this.productId, data).subscribe(resp => {
      if (!resp) {
        return;
      }
      this.loading = false;
      if (resp && resp.success) {
        this.isPublished = resp.data.status === 'publish';
        this._createVariations(this.productId);
        NotifierComponent.notify(new Notification(TextUtils.getText('text_productWasSuccessfullyUpdated'), NotificationType.SUCCESS));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.EDIT);
        // this._setDefaultWeightType();
      } else {
        NotifierComponent.notify(new Notification(TextUtils.getText('text_failedToUpdateTheProduct'), NotificationType.DANGER));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.EDIT, true);
      }
    });
  }

  private async _createProduct() {
    const formValue = this.productForm.getRawValue();
    let images = [];
    if (formValue.media_group.images.length) {
      images = await this._productAddHelper.uploadImages(formValue, this.website.id);
    }

    if (this.activeAttributes.length) {
      await this._saveAttributes(this.activeAttributes, false);
    }
    const attributes = this._productAddHelper.processAttributesForm(this.variantsForm.getRawValue().variants);
    const data = this._productAddHelper.processFormData(formValue, images, attributes);
    this._ecommerceService.createProduct(this.website.id, data).subscribe(resp => {
      if (!resp) {
        return;
      }
      this.loading = false;
      if (resp && resp.success) {
        this._navigateToList();
        this._createVariations(resp.data.dataKey.id);
        // this._setDefaultWeightType();
        this._store.dispatch(new fromStore.LoadEcommerceSetupList({websiteId: this.website.id}));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CREATE);
      } else {
        NotifierComponent.notify(new Notification(TextUtils.getText('text_failedToCreateTheProduct'), NotificationType.DANGER));
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.CREATE, true);
      }
    });
  }

  discardForm() {
    // this._discardChanges();
    this._navigateToList();
  }

  private _navigateToList() {
    this._router.navigate(['.'], {relativeTo: this._activatedRoute.parent, queryParams: {status: 'any'}});
  }

  ngOnDestroy() {
    this._subscription$.next();
    this._subscription$.complete();
  }

}
